window.NX1Search = {
  query(value) {
    const term = String(value || '').trim();
    if (!/[a-z]/i.test(term) || /["\\]/.test(term) || /(?:^|\s)(?:title|tag|body|vendor|product_type):/i.test(term)) return term;
    const matches = [...term.matchAll(/(?:^|\s)(#?)(\d{1,4})(?=\s|$)/g)];
    const issue = matches.find(match => (match[1] || Number(match[2]) < 1900));
    if (!issue) return term;
    const words = text => text.match(/[\p{L}\p{N}]+(?:-[\p{L}\p{N}]+)*/gu) || [];
    const beforeIssue = words(term.slice(0, issue.index));
    const afterIssue = words(term.slice(issue.index + issue[0].length));
    const title = beforeIssue.length ? beforeIssue : afterIssue;
    if (!title.length) return term;
    const suffix = beforeIssue.length ? afterIssue : [];
    return [...title.slice(0, -1), title.at(-1) + ' #' + issue[2]].map(token => 'title:"' + token + '"').concat(
      suffix.map(token => '(title:"' + token + '" OR tag:"' + token + '")')
    ).join(' AND ');
  },
  async correct(value, signal) {
    const term = String(value || '').trim();
    const issue = [...term.matchAll(/(?:^|\s)(#?)(\d{1,4})(?=\s|$)/g)].find(match => (match[1] || Number(match[2]) < 1900));
    if (!issue || this.query(term) === term) return term;
    const words = text => (text.toLowerCase().match(/[a-z0-9]+/g) || []);
    const close = (a, b) => {
      if (b.startsWith(a)) return true;
      if (a.length < 4 || Math.abs(a.length - b.length) > 1) return false;
      let row = Array.from({length:b.length + 1}, (_, i) => i);
      for (let i = 1; i <= a.length; i++) {
        const next = [i];
        for (let j = 1; j <= b.length; j++) next[j] = Math.min(next[j - 1] + 1, row[j] + 1, row[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
        row = next;
      }
      return row[b.length] <= 1;
    };
    const response = await fetch(routes.predictive_search_url + '.json?q=' + encodeURIComponent(term) + '&resources[type]=product', {signal});
    if (!response.ok) return term;
    const data = await response.json();
    // Recover only one series for this exact issue. Artist/variant words may
    // precede, follow or interrupt the title; none may be discarded.
    const tokens = (term.slice(0, issue.index) + ' ' + term.slice(issue.index + issue[0].length)).match(/[\p{L}\p{N}]+(?:-[\p{L}\p{N}]+)*/gu) || [];
    const wanted = tokens.map(token => words(token).join(' '));
    const candidates = new Map();
    let bestScore = 0;
    for (const product of data.resources?.results?.products || []) {
      const match = product.title.match(/^(.+?)\s*#(\d+)\b/);
      if (!match || Number(match[2]) !== Number(issue[2])) continue;
      const series = words(match[1]);
      if (!series.length) continue;
      const consumed = wanted.map(word => {
        const parts = words(word);
        return parts.length > 0 && parts.every(part => series.some(candidate => close(part, candidate)));
      });
      // Require the title word adjacent to #issue, not merely a shared article.
      if (!wanted.some(word => words(word).some(part => close(part, series.at(-1))))) continue;
      const score = consumed.filter(Boolean).length;
      if (!score || score < bestScore) continue;
      if (score > bestScore) { candidates.clear(); bestScore = score; }
      const extras = tokens.filter((_, index) => !consumed[index]);
      // Shopify does not index a leading standalone "The" title constraint.
      const recoveredSeries = match[1].trim().replace(/^the\s+/i, '');
      candidates.set(match[1].trim().toLowerCase(), recoveredSeries + ' ' + issue[2] + (extras.length ? ' ' + extras.join(' ') : ''));
    }
    if (candidates.size !== 1) return term;
    return [...candidates.values()][0];
  },
  display(value) {
    const term = String(value || '');
    if (!term.startsWith('title:"') && !term.startsWith('(title:')) return term;
    return [...new Set([...term.matchAll(/(?:title|tag|body):(?:"([^"\\]+)"|([\p{L}\p{N}-]+)\*?)/gu)].map(match => match[1] || match[2]))].join(' ');
  }
};
Object.assign(window.NX1Search, {
  contexts: new Map(),
  normalizeArtist(value) {
    return String(value).replace(/&#(?:0*39|x27);|&apos;/gi, "'").normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().match(/[\p{L}\p{N}]+/gu) || [];
  },
  async context(query, signal) {
    if (this.contexts.has(query)) return this.contexts.get(query);
    const params = new URLSearchParams({q:query,type:'product',section_id:'nx1-search-context'});
    const response = await fetch(routes.predictive_search_url.replace('/suggest', '') + '?' + params, {signal});
    if (!response.ok) throw new Error(response.status);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const data = JSON.parse(doc.querySelector('[data-nx1-search-context]').textContent);
    if (this.contexts.size > 50) this.contexts.clear();
    this.contexts.set(query, data);
    return data;
  },
  artistMatch(term, values) {
    const tokens = this.normalizeArtist(term);
    const groups = new Map();
    for (const value of values) {
      const name = this.normalizeArtist(value);
      if (!name.length) continue;
      let used = [];
      for (const word of name) {
        const index = tokens.findIndex((token, i) => !used.includes(i) && (token === word || (token.length >= 3 && word.startsWith(token))));
        if (index !== -1) used.push(index);
      }
      const full = used.length === name.length;
      const surname = name.length >= 3 && name.slice(-2).every(word => tokens.includes(word));
      if (!full && !surname) continue;
      if (!full) used = tokens.map((token,i)=>name.slice(-2).includes(token)?i:-1).filter(i=>i>=0);
      const key = name.join(' ');
      const existing = groups.get(key);
      if (existing) existing.values.push(value);
      else groups.set(key,{values:[value],used});
    }
    const matches = [...groups.values()].sort((a,b)=>b.used.length-a.used.length);
    if (!matches.length || (matches[1] && matches[0].used.length === matches[1].used.length)) return null;
    const best = matches[0];
    best.remaining = tokens.filter((_,i)=>!best.used.includes(i)).join(' ');
    return best;
  },
  async resolve(value, signal) {
    const term = this.display(String(value || '').trim());
    const params = new URLSearchParams({q:this.query(term),type:'product','options[prefix]':'last'});
    if (!term || /(?:title|tag|body|vendor):/i.test(term)) return params;
    const [global, scoped] = await Promise.all([this.context('*',signal),this.context(term,signal)]);
    const artist = this.artistMatch(term,[...new Set([...global.artists,...scoped.artists])]);
    let remaining = artist ? artist.remaining : term;
    let query = remaining ? this.query(remaining) : '*';
    if (artist) artist.values.forEach(name=>params.append('filter.p.m.custom.cover_artist',name));
    if (query !== remaining && remaining) {
      const strict = await this.context(query,signal);
      if (strict.count === 0) query = this.query(await this.correct(remaining,signal));
    }
    params.set('q',query);
    return params;
  },
  pageDisplay(value) {
    let term = this.display(value);
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('q') || '';
    if (value !== raw && value !== this.display(raw)) return term;
    const artists = params.getAll('filter.p.m.custom.cover_artist');
    if (!artists.length) return term;
    const names = [...new Map(artists.map(name=>[this.normalizeArtist(name).join(' '),name])).values()];
    if (term === '*') term = '';
    for (const name of names) {
      if (!this.normalizeArtist(name).every(word=>this.normalizeArtist(term).includes(word))) term += ' ' + name;
    }
    return term.trim();
  }
});


// Staged native-tag path: enable only with nx1_artist_mode=native in draft QA.
// Directory is generated from the complete catalog by the tag planner, never filter values.
(() => {
 const search=window.NX1Search, previousResolve=search.resolve, previousDisplay=search.display;
 // Shopify pagination/filter URLs preserve q, but can omit custom parameters.
 // The dedicated namespace makes an existing native artist query self-describing.
 search.nativeMode=function(params=new URLSearchParams(window.location.search)){
  return params.get('nx1_artist_mode')==='native'||/tag:"(?:nx1-cover-artist:[^"]+|nx1cav1[0-9a-f]{64})"/.test(params.get('q')||'');
 };
 search.display=function(value){
  const text=String(value||'').replace(/ OR tag:"(?:nx1-cover-artist:[^"]+|nx1cav1[0-9a-f]{64})"/g,'');
  if(/^\(*title:/.test(text))return [...new Set([...text.matchAll(/title:"([^"]+)"/g)].map(m=>m[1]))].join(' ');
  return previousDisplay.call(this,text);
 };
 search.directory=async function(signal){
  if(this.artistDirectory)return this.artistDirectory;
  const context=await this.context('*',signal);
  if(!context.directory_url)throw new Error('Artist directory unavailable');
  const response=await fetch(context.directory_url,{signal});
  if(!response.ok)throw new Error('Artist directory unavailable');
  const data=await response.json();if(data.version!==1||!Array.isArray(data.names))throw new Error('Invalid artist directory');
  this.artistDirectory=data.names;return data.names;
 };
 // Must match plan-tags.cjs: versioned SHA-256 of the normalized word array.
 // Word boundaries prevent concatenation collisions such as Ann Ab / Anna B.
 search.artistTag=async function(name){
  const canonical=JSON.stringify(['nx1-cover-artist',1,this.normalizeArtist(name)]);
  const digest=await crypto.subtle.digest('SHA-256',new TextEncoder().encode(canonical));
  return 'nx1cav1'+Array.from(new Uint8Array(digest),byte=>byte.toString(16).padStart(2,'0')).join('');
 };
 search.resolve=async function(value,signal){
  if(!this.nativeMode())return previousResolve.call(this,value,signal);
  const raw=String(value||'').trim();
  const params=new URLSearchParams({type:'product','options[prefix]':'last',nx1_artist_mode:'native'});
  if(/(?:title|tag|body|vendor):/i.test(raw)){params.set('q',raw);return params;}
  const term=this.display(raw),artist=this.artistMatch(term,await this.directory(signal));
  const remaining=artist?artist.remaining:term;
  let query=remaining?this.query(remaining):'';
  if(query!==remaining&&remaining&&(await this.context(query,signal)).count===0)query=this.query(await this.correct(remaining,signal));
  if(artist){
   const words=this.normalizeArtist(artist.values[0]);
   const title=words.map(w=>'title:"'+w+'"').join(' AND ');
   const credit='(('+title+') OR tag:"'+await this.artistTag(artist.values[0])+'")';
   query=query?'('+query+') AND '+credit:credit;
  }
  params.set('q',query||term);return params;
 };
})();

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.dataset.nx1SearchReady = 'true';
      this.input.value = window.NX1Search.pageDisplay(this.input.value);
      this.input.form.addEventListener('submit', async (event) => {
        if (this.querySelector('[aria-selected="true"] a') || event.nx1Resolved) return;
        event.preventDefault();
        event.nx1Resolved = true;
        const term = this.input.value;
        try {
          const resolved = await window.NX1Search.resolve(term);
          if (this.input.value !== term) return;
          const url = new URL(this.input.form.action);
          for (const [key,value] of new FormData(this.input.form)) url.searchParams.append(key,value);
          for (const key of new Set(resolved.keys())) url.searchParams.delete(key);
          for (const [key,value] of resolved) url.searchParams.append(key,value);
          window.location.assign(url.pathname + url.search);
        } catch (error) { console.warn('[NX1] Search unavailable; please retry'); }
      });

      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);


/* NX1 Coming Soon search augmentation.
 * Keeps future-release products DRAFT/unpublished while making their public
 * preview cards discoverable from storefront search and predictive search.
 */
(() => {
  if (window.NX1ComingSoonSearchLoaded) return;
  window.NX1ComingSoonSearchLoaded = true;

  const PAGE_URL = '/pages/coming-soon';
  const FULL_RESULTS_ID = 'nx1-coming-soon-search';
  const PREDICTIVE_CLASS = 'nx1-predictive-coming-soon';
  let indexPromise = null;
  let fullRenderTimer = null;

  function compact(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function normalize(value) {
    return compact(value)
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function decodeCaptured(value) {
    return String(value || '')
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, '\\');
  }

  function unwrapSearchTerm(raw) {
    const value = window.NX1Search.display(compact(raw));
    const generated =
      /^\(title:"((?:\\.|[^"])*)" OR vendor:"(?:\\.|[^"])*" OR tag:"(?:\\.|[^"])*"\)$/i;
    const oldTitle = /^title:"((?:\\.|[^"])*)"$/i;

    let match = value.match(generated);
    if (match) return decodeCaptured(match[1]);

    match = value.match(oldTitle);
    if (match) return decodeCaptured(match[1]);

    return value;
  }

  function queryTokens(term) {
    return normalize(term)
      .split(' ')
      .filter((token) => token.length > 1 || /^\d+$/.test(token));
  }

  function matches(item, term) {
    const tokens = queryTokens(term);
    if (!tokens.length) return false;
    const haystack = normalize(item.title + ' ' + item.vendor);
    return tokens.every((token) => /^\d+$/.test(token) ? haystack.split(' ').includes(token) : haystack.includes(token));
  }

  function parseIndex(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const items = [];

    doc.querySelectorAll('main h3, article h3').forEach((titleNode) => {
      let article = titleNode.parentElement;
      while (article && !(article.querySelector('img') && article.querySelectorAll('h3').length === 1 && /preview only/i.test(article.textContent))) {
        article = article.parentElement;
      }
      if (!article) return;
      const imageNode = article.querySelector('img');
      const text = compact(article.textContent);

      if (!titleNode || !imageNode || !/preview only/i.test(text)) return;

      const title = compact(titleNode.textContent);
      const vendor = compact(titleNode.previousElementSibling?.textContent);
      const priceMatch = text.match(/\$\d+(?:\.\d{2})?/);
      const releaseMatch = text.match(/(?:Available|Release date:)\s+([A-Za-z]+\s+\d{1,2},\s+\d{4})/i);

      if (!title) return;

      items.push({
        title,
        vendor,
        price: priceMatch ? priceMatch[0] : '',
        release: releaseMatch ? releaseMatch[1] : '',
        image: imageNode.currentSrc || imageNode.getAttribute('src') || '',
      });
    });

    return items;
  }

  function getIndex() {
    if (!indexPromise) {
      indexPromise = fetch(PAGE_URL, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      })
        .then((response) => {
          if (!response.ok) throw new Error('Coming Soon page returned ' + response.status);
          return response.text();
        })
        .then(parseIndex)
        .catch((error) => {
          console.warn('[NX1] Coming Soon search unavailable:', error);
          return [];
        });
    }
    return indexPromise;
  }

  function rank(items, term) {
    const needle = normalize(term);
    return items
      .filter((item) => matches(item, term))
      .sort((a, b) => {
        const at = normalize(a.title);
        const bt = normalize(b.title);
        const aStarts = at.startsWith(needle) ? 0 : at.includes(needle) ? 1 : 2;
        const bStarts = bt.startsWith(needle) ? 0 : bt.includes(needle) ? 1 : 2;
        return aStarts - bStarts || at.localeCompare(bt);
      });
  }

  function currentFullSearchTerm() {
    const raw =
      new URLSearchParams(window.location.search).get('q') ||
      document.querySelector('#Search-In-Template')?.value ||
      '';
    return window.NX1Search.pageDisplay(unwrapSearchTerm(raw));
  }

  function nativeFullTitles() {
    return new Set(
      Array.from(document.querySelectorAll('#ProductGridContainer .card__heading a'))
        .map((node) => normalize(node.textContent))
        .filter(Boolean)
    );
  }

  function nativePredictiveTitles(target) {
    return new Set(
      Array.from(target.querySelectorAll('.predictive-search__item-heading'))
        .map((node) => normalize(node.textContent))
        .filter(Boolean)
    );
  }

  function resultHref(item) {
    return PAGE_URL + '?find=' + encodeURIComponent(item.title);
  }

  function ensureStyles() {
    if (document.getElementById('nx1-coming-soon-search-styles')) return;

    const style = document.createElement('style');
    style.id = 'nx1-coming-soon-search-styles';
    style.textContent = [
      '.nx1-cs-results{margin:0 0 3.2rem;padding:0 0 3.2rem;border-bottom:1px solid rgba(255,255,255,.12)}',
      '.nx1-cs-heading{display:flex;align-items:end;justify-content:space-between;gap:1.5rem;margin:0 0 1.6rem}',
      '.nx1-cs-heading h2{margin:0;color:rgb(var(--color-foreground));font-size:2.2rem}',
      '.nx1-cs-heading p{margin:0;color:rgba(var(--color-foreground),.7);font-size:1.3rem}',
      '.nx1-cs-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:1.4rem}',
      '.nx1-cs-card{display:flex;flex-direction:column;height:100%;text-decoration:none;color:rgb(var(--color-foreground));background:#161616;border:1px solid rgba(255,255,255,.13);border-radius:10px;overflow:hidden;transition:transform .18s ease,border-color .18s ease}',
      '.nx1-cs-card:hover{transform:translateY(-3px);border-color:rgba(91,121,235,.62)}',
      '.nx1-cs-media{position:relative;aspect-ratio:2/3;background:#111;overflow:hidden}',
      '.nx1-cs-media img{display:block;width:100%;height:100%;object-fit:contain}',
      '.nx1-cs-badge{position:absolute;top:.8rem;right:.8rem;padding:.55rem .8rem;border-radius:999px;background:#334fb4;color:#fff;font-size:1.05rem;font-weight:700;letter-spacing:.03em}',
      '.nx1-cs-info{display:flex;flex-direction:column;flex:1;padding:1.2rem}',
      '.nx1-cs-vendor{margin:0 0 .55rem;color:rgba(var(--color-foreground),.66);font-size:1.05rem;text-transform:uppercase;letter-spacing:.07em}',
      '.nx1-cs-title{margin:0 0 .9rem;color:rgb(var(--color-foreground));font-size:1.45rem;line-height:1.35;font-weight:700}',
      '.nx1-cs-price{margin-top:auto;font-size:1.55rem;font-weight:700}',
      '.nx1-cs-release{margin-top:.55rem;color:rgba(var(--color-foreground),.72);font-size:1.15rem}',
      '.nx1-predictive-coming-soon{border-top:.1rem solid rgba(var(--color-foreground),.08)}',
      '.nx1-predictive-meta{margin-top:.35rem;color:rgba(var(--color-foreground),.68);font-size:1.1rem}',
      '.nx1-predictive-badge{display:inline-block;margin-left:.5rem;padding:.2rem .55rem;border-radius:999px;background:#334fb4;color:#fff;font-size:.95rem;font-weight:700;vertical-align:middle}',
      '.nx1-coming-soon-focus{outline:3px solid #334fb4!important;outline-offset:4px!important}',
      '@media(max-width:989px){.nx1-cs-grid{grid-template-columns:repeat(3,minmax(0,1fr))}}',
      '@media(max-width:749px){.nx1-cs-heading{display:block}.nx1-cs-heading p{margin-top:.5rem}.nx1-cs-grid{grid-template-columns:repeat(2,minmax(0,1fr));gap:1rem}}',
    ].join('');
    document.head.appendChild(style);
  }

  function makeFullCard(item) {
    const anchor = document.createElement('a');
    anchor.className = 'nx1-cs-card';
    anchor.href = resultHref(item);
    anchor.setAttribute('aria-label', item.title + ' — Coming Soon');

    const media = document.createElement('div');
    media.className = 'nx1-cs-media';

    const image = document.createElement('img');
    image.src = item.image;
    image.alt = item.title;
    image.loading = 'lazy';
    media.appendChild(image);

    const badge = document.createElement('span');
    badge.className = 'nx1-cs-badge';
    badge.textContent = 'Coming Soon';
    media.appendChild(badge);

    const info = document.createElement('div');
    info.className = 'nx1-cs-info';

    if (item.vendor) {
      const vendor = document.createElement('p');
      vendor.className = 'nx1-cs-vendor';
      vendor.textContent = item.vendor;
      info.appendChild(vendor);
    }

    const title = document.createElement('h3');
    title.className = 'nx1-cs-title';
    title.textContent = item.title;
    info.appendChild(title);

    if (item.price) {
      const price = document.createElement('div');
      price.className = 'nx1-cs-price';
      price.textContent = item.price;
      info.appendChild(price);
    }

    const release = document.createElement('div');
    release.className = 'nx1-cs-release';
    release.textContent = item.release ? 'Available ' + item.release : 'Preview only';
    info.appendChild(release);

    anchor.append(media, info);
    return anchor;
  }

  async function renderFullSearch() {
    if (!/\/search\/?$/.test(window.location.pathname)) return;

    const container = document.querySelector('#ProductGridContainer');
    if (!container) return;

    const term = currentFullSearchTerm();
    const existing = document.getElementById(FULL_RESULTS_ID);

    if (term.length < 2) {
      existing?.remove();
      return;
    }

    const items = await getIndex();
    const nativeTitles = nativeFullTitles();
    const matchesForTerm = rank(items, term)
      .filter((item) => !nativeTitles.has(normalize(item.title)))
      .slice(0, 24);

    existing?.remove();

    if (!matchesForTerm.length) return;

    const section = document.createElement('section');
    section.id = FULL_RESULTS_ID;
    section.className = 'nx1-cs-results';
    section.dataset.term = normalize(term);

    const heading = document.createElement('div');
    heading.className = 'nx1-cs-heading';

    const title = document.createElement('h2');
    title.textContent = 'Coming Soon';

    const note = document.createElement('p');
    note.textContent =
      matchesForTerm.length +
      (matchesForTerm.length === 1 ? ' preview match' : ' preview matches') +
      ' — not purchasable before release';

    heading.append(title, note);

    const grid = document.createElement('div');
    grid.className = 'nx1-cs-grid';
    matchesForTerm.forEach((item) => grid.appendChild(makeFullCard(item)));

    section.append(heading, grid);
    container.prepend(section);

    const searchRoot = document.querySelector('.template-search');
    if (searchRoot?.classList.contains('template-search--empty')) {
      searchRoot.classList.remove('template-search--empty');
      document
        .querySelectorAll('.template-search__header p[role="status"]')
        .forEach((node) => {
          node.hidden = true;
        });
    }
  }

  function scheduleFullSearch() {
    clearTimeout(fullRenderTimer);
    fullRenderTimer = setTimeout(renderFullSearch, 80);
  }

  function makePredictiveItem(item, index) {
    const li = document.createElement('li');
    li.className = 'predictive-search__list-item';
    li.setAttribute('role', 'option');
    li.setAttribute('aria-selected', 'false');
    li.id = 'nx1-predictive-coming-soon-' + index;

    const anchor = document.createElement('a');
    anchor.className =
      'predictive-search__item predictive-search__item--link-with-thumbnail link link--text';
    anchor.href = resultHref(item);
    anchor.tabIndex = -1;

    const image = document.createElement('img');
    image.className = 'predictive-search__image';
    image.src = item.image;
    image.alt = item.title;
    image.width = 50;
    image.height = 75;
    image.loading = 'lazy';

    const content = document.createElement('div');
    content.className = 'predictive-search__item-content';

    const title = document.createElement('p');
    title.className = 'predictive-search__item-heading h5';
    title.textContent = item.title;

    const badge = document.createElement('span');
    badge.className = 'nx1-predictive-badge';
    badge.textContent = 'Coming Soon';
    title.appendChild(badge);

    const meta = document.createElement('div');
    meta.className = 'nx1-predictive-meta';
    meta.textContent = [item.vendor, item.release ? 'Available ' + item.release : '']
      .filter(Boolean)
      .join(' • ');

    content.append(title, meta);
    anchor.append(image, content);
    li.appendChild(anchor);
    return li;
  }

  async function renderPredictive(host) {
    const target = host.querySelector('[data-predictive-search]');
    const input = host.querySelector('input[type="search"]');
    if (!target || !input) return;

    const term = unwrapSearchTerm(input.value);
    const termKey = normalize(term);
    const current = target.querySelector('.' + PREDICTIVE_CLASS);

    if (term.length < 2) {
      current?.remove();
      return;
    }

    if (current?.dataset.term === termKey) return;

    const items = await getIndex();
    const nativeTitles = nativePredictiveTitles(target);
    const matchesForTerm = rank(items, term)
      .filter((item) => !nativeTitles.has(normalize(item.title)))
      .slice(0, 5);

    target.querySelector('.' + PREDICTIVE_CLASS)?.remove();
    if (!matchesForTerm.length) return;

    const wrapper = document.createElement('div');
    wrapper.className = PREDICTIVE_CLASS;
    wrapper.dataset.term = termKey;

    const heading = document.createElement('h2');
    heading.className = 'predictive-search__heading text-body caption-with-letter-spacing';
    heading.textContent = 'Coming Soon';

    const list = document.createElement('ul');
    list.className = 'predictive-search__results-list list-unstyled';
    list.setAttribute('role', 'group');
    list.setAttribute('aria-label', 'Coming Soon');

    matchesForTerm.forEach((item, index) => {
      list.appendChild(makePredictiveItem(item, index));
    });

    wrapper.append(heading, list);

    const searchButton = target.querySelector('.predictive-search__search-for-button');
    if (searchButton) {
      searchButton.before(wrapper);
    } else {
      target.appendChild(wrapper);
    }
  }

  function setupPredictiveSearch() {
    document.querySelectorAll('predictive-search').forEach((host) => {
      if (host.dataset.nx1ComingSoonBound === 'true') return;
      host.dataset.nx1ComingSoonBound = 'true';

      const target = host.querySelector('[data-predictive-search]');
      const input = host.querySelector('input[type="search"]');
      if (!target || !input) return;

      let timer = null;
      const schedule = () => {
        clearTimeout(timer);
        timer = setTimeout(() => renderPredictive(host), 120);
      };

      input.addEventListener('input', schedule);
      input.addEventListener('focus', schedule);

      const observer = new MutationObserver(() => {
        const termKey = normalize(unwrapSearchTerm(input.value));
        const section = target.querySelector('.' + PREDICTIVE_CLASS);
        if (!section || section.dataset.term !== termKey) schedule();
      });
      observer.observe(target, { childList: true, subtree: true });
    });
  }

  function focusComingSoonCard() {
    if (!/\/pages\/coming-soon\/?$/.test(window.location.pathname)) return;

    const requested = new URLSearchParams(window.location.search).get('find');
    if (!requested) return;

    const wanted = normalize(requested);
    const article = Array.from(document.querySelectorAll('article')).find((node) => {
      return normalize(node.querySelector('h3')?.textContent) === wanted;
    });

    if (!article) return;

    article.classList.add('nx1-coming-soon-focus');
    article.tabIndex = -1;

    requestAnimationFrame(() => {
      article.scrollIntoView({ behavior: 'smooth', block: 'center' });
      article.focus({ preventScroll: true });
    });
  }

  function initComingSoonSearch() {
    ensureStyles();
    setupPredictiveSearch();
    focusComingSoonCard();
    scheduleFullSearch();

    const searchRoot = document.querySelector('.template-search');
    if (searchRoot) {
      const observer = new MutationObserver(() => {
        if (!document.getElementById(FULL_RESULTS_ID)) scheduleFullSearch();
      });
      observer.observe(searchRoot, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initComingSoonSearch, { once: true });
  } else {
    initComingSoonSearch();
  }
})();


window.addEventListener('pageshow', () => {
  document.querySelectorAll('input[type="search"]').forEach(input => {
    input.value = window.NX1Search.pageDisplay(input.value);
  });
});
const nx1ArtistEditedForms = new WeakSet();
document.addEventListener('input', event => {
  if (event.target.name === 'filter.p.m.custom.cover_artist' && event.target.form) {
    nx1ArtistEditedForms.add(event.target.form);
  }
}, true);
document.addEventListener('formdata', event => {
  if (!event.target.closest('facet-filters-form') || !event.formData.has('q')) return;
  if (window.NX1Search.nativeMode()) event.formData.set('nx1_artist_mode', 'native');
  const key = 'filter.p.m.custom.cover_artist';
  const selected = event.formData.getAll(key);
  const active = [...new Set(new URLSearchParams(window.location.search).getAll(key))];
  const artistEdited = nx1ArtistEditedForms.has(event.target);
  nx1ArtistEditedForms.delete(event.target);
  for (const value of active) {
    if (!selected.includes(value) && ((!selected.length && !artistEdited) || selected.some(name => window.NX1Search.normalizeArtist(name).join(' ') === window.NX1Search.normalizeArtist(value).join(' ')))) {
      event.formData.append(key, value);
    }
  }
});
(async () => {
  if (!document.getElementById('Search-In-Template')) return;
  const url = new URL(window.location.href);
  const raw = url.searchParams.get('q') || '';
  document.querySelectorAll('.template-search__header p[role="status"]').forEach(node => {
    node.textContent = node.textContent.replace(raw, window.NX1Search.pageDisplay(raw));
  });
  if (url.searchParams.has('filter.p.m.custom.cover_artist')) return;
  // Already-resolved field queries are the authoritative native search state.
  // Reconstructing them from display text discards constraints and resets page 2.
  if (/(?:title|tag|body|vendor):/i.test(raw)) return;
  try {
    const resolved = await window.NX1Search.resolve(window.NX1Search.display(raw));
    let changed = false;
    for (const key of new Set(resolved.keys())) {
      if (JSON.stringify(url.searchParams.getAll(key)) !== JSON.stringify(resolved.getAll(key))) {
        url.searchParams.delete(key);
        resolved.getAll(key).forEach(value=>url.searchParams.append(key,value));
        changed=true;
      }
    }
    if (changed) {
      url.searchParams.delete('page');
      window.location.replace(url.pathname + url.search);
    }
  } catch (error) { console.warn('[NX1] Search correction unavailable'); }
})();
